// src/components/apps/ghouseishosi/GhouseishosiBottomNav.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Box,
    keyframes
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import QuizIcon from '@mui/icons-material/Quiz';
import SettingsIcon from '@mui/icons-material/Settings';
import GavelIcon from '@mui/icons-material/Gavel';
import { useNavigate, useLocation } from 'react-router-dom';

export const NAV_HEIGHT = 72;

// ゆらゆらアニメーション
const sway = keyframes`
  0%, 100% {
    transform: translateX(0px);
    opacity: 0.4;
  }
  50% {
    transform: translateX(-3px);
    opacity: 0.7;
  }
`;

const GhouseishosiBottomNav: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const appId = 'ghouseishosi';
    const scrollRef = useRef<HTMLDivElement>(null);

    const getInitialValue = () => {
        if (location.pathname === `/${appId}` || location.pathname === `/${appId}/`) return 0;
        if (location.pathname.startsWith(`/${appId}/subjects`)) return 1;
        if (location.pathname.startsWith(`/${appId}/practice`)) return 2;
        if (location.pathname.startsWith(`/${appId}/settings`)) return 3;
        return 0;
    };

    const [value, setValue] = useState(getInitialValue());
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    useEffect(() => {
        if (location.pathname === `/${appId}` || location.pathname === `/${appId}/`) setValue(0);
        else if (location.pathname.startsWith(`/${appId}/subjects`)) setValue(1);
        else if (location.pathname.startsWith(`/${appId}/practice`)) setValue(2);
        else if (location.pathname.startsWith(`/${appId}/settings`)) setValue(3);
        else setValue(0);
    }, [location.pathname]);

    // スクロール可能かどうかをチェック
    const checkScrollable = () => {
        const el = scrollRef.current;
        if (!el) return;

        const isScrollable = el.scrollWidth > el.clientWidth;
        const isAtStart = el.scrollLeft <= 1;
        const isAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

        setShowLeftArrow(isScrollable && !isAtStart);
        setShowRightArrow(isScrollable && !isAtEnd);
    };

    // 初期チェックとリサイズ時のチェック
    useEffect(() => {
        // DOMが完全にレンダリングされた後にチェック
        const timer = setTimeout(checkScrollable, 100);
        window.addEventListener('resize', checkScrollable);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', checkScrollable);
        };
    }, []);

    // 値が変更されたときもチェック
    useEffect(() => {
        checkScrollable();
    }, [value]);

    // スクロールイベント
    const handleScroll = () => {
        checkScrollable();
    };

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        switch (newValue) {
            case 0:
                navigate(`/${appId}`);
                break;
            case 1:
                navigate(`/${appId}/subjects`);
                break;
            case 2:
                navigate(`/${appId}/practice`);
                break;
            case 3:
                navigate(`/${appId}/settings`);
                break;
            default:
                navigate(`/${appId}`);
        }
    };

    const commonSx = {
        flex: '1 1 0',
        minWidth: 0,
        maxWidth: 'none',
        color: 'rgba(255,255,255,0.8)',
        position: 'relative',
        '&.Mui-selected': {
            color: '#fff',
            backgroundColor: '#006400' // 行政書士用の色（緑系）
        }
    };

    return (
        <Paper
            sx={{
                position: 'fixed',
                inset: `auto 0 0 0`,
                paddingBottom: 'env(safe-area-inset-bottom)',
                zIndex: 1200
            }}
            elevation={3}
            square
        >
            <Box sx={{ position: 'relative' }}>
                {/* 左側の三角インジケーター */}
                {showLeftArrow && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            pointerEvents: 'none',
                            animation: `${sway} 2s ease-in-out infinite`,
                            transformOrigin: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderRight: '12px solid rgba(255, 255, 255, 0.6)',
                            }}
                        />
                    </Box>
                )}

                {/* 右側の三角インジケーター */}
                {showRightArrow && (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%) scaleX(-1)',
                            zIndex: 1,
                            pointerEvents: 'none',
                            animation: `${sway} 2s ease-in-out infinite`,
                            transformOrigin: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderRight: '12px solid rgba(255, 255, 255, 0.6)',
                            }}
                        />
                    </Box>
                )}

                <Box
                    ref={scrollRef}
                    onScroll={handleScroll}
                    sx={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none'
                    }}
                >
                    <BottomNavigation
                        value={value}
                        onChange={handleChange}
                        showLabels
                        sx={{
                            backgroundColor: '#2E8B57', // 行政書士用の色（シーグリーン）
                            minHeight: NAV_HEIGHT,
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'flex-start',
                            minWidth: 'max-content'
                        }}
                    >
                        <BottomNavigationAction
                            label="ホーム"
                            icon={<HomeIcon />}
                            sx={commonSx}
                        />
                        <BottomNavigationAction
                            label="科目"
                            icon={<QuizIcon />}
                            sx={commonSx}
                        />
                        <BottomNavigationAction
                            label="実務練習"
                            icon={<GavelIcon />}
                            sx={commonSx}
                        />
                        <BottomNavigationAction
                            label="設定"
                            icon={<SettingsIcon />}
                            sx={commonSx}
                        />
                    </BottomNavigation>
                </Box>
            </Box>
        </Paper>
    );
};

export default GhouseishosiBottomNav;
